import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Paper,
  Grid
} from '@mui/material';
import {
  HistoryOutlined as HistoryIcon,
  RestoreOutlined as RestoreIcon,
  CloseOutlined as CloseIcon,
  VisibilityOutlined as VisibilityIcon,
  PersonOutlined as PersonIcon,
  ScheduleOutlined as ScheduleIcon
} from '@mui/icons-material';
import { getPromptVersions, restorePromptVersion, getContext } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useSessionData } from '../../../contexts/SessionDataContext';
import ContextDialog from '../../context-repository/ContextDialog';
import { PRODUCT_TERMS } from '../../../constants/productTerminology';

const VersionHistoryDialog = ({ open, onClose, prompt, onRestore }) => {
  const { user } = useAuth();
  const { getGroups } = useSessionData();
  const [userGroups, setUserGroups] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState(null);
  const [showVersionDetails, setShowVersionDetails] = useState(false);
  const [contexts, setContexts] = useState({}); // Store loaded contexts by ID
  const [loadingContexts, setLoadingContexts] = useState({}); // Track which contexts are loading
  const [previewContext, setPreviewContext] = useState(null);
  const [showContextPreview, setShowContextPreview] = useState(false);

  // Fetch user groups on mount (session cache)
  useEffect(() => {
    let cancelled = false;
    const fetchUserGroups = async () => {
      if (!user?.companyId || !user?.id) return;
      try {
        const groups = await getGroups({ force: false });
        if (!cancelled) setUserGroups(Array.isArray(groups) ? groups : []);
      } catch (error) {
        console.error('Error fetching user groups:', error);
        if (!cancelled) setUserGroups([]);
      }
    };
    if (user) fetchUserGroups();
    return () => {
      cancelled = true;
    };
  }, [user, getGroups]);

  // Check if user can restore versions (creator OR has edit/owner permission)
  // userGroups contains groups where user is a member (via memberIds or teamIds)
  const userGroupIds = userGroups.map(g => g.id);
  const canRestore = user && prompt && (
    user.id === prompt.userId || 
    user.role === 'ADMIN' || 
    user.role === 'OWNER' ||
    (prompt.permissions && (
      // Check edit permission (direct user, team, or group)
      (prompt.permissions.canEdit && (
        prompt.permissions.canEdit.includes(user.id) ||
        (user.teamId && prompt.permissions.canEdit.includes(user.teamId)) ||
        (userGroupIds.length > 0 && userGroupIds.some(groupId => prompt.permissions.canEdit.includes(groupId)))
      )) ||
      // Check owner permission (canDelete array) - direct user, team, or group
      (prompt.permissions.canDelete && (
        prompt.permissions.canDelete.includes(user.id) ||
        (user.teamId && prompt.permissions.canDelete.includes(user.teamId)) ||
        (userGroupIds.length > 0 && userGroupIds.some(groupId => prompt.permissions.canDelete.includes(groupId)))
      ))
    ))
  );

  useEffect(() => {
    if (open && prompt) {
      loadVersions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prompt]);

  // Load contexts when version details dialog opens
  useEffect(() => {
    if (showVersionDetails && selectedVersion) {
      loadContextsForVersion(selectedVersion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showVersionDetails, selectedVersion]);

  const loadVersions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getPromptVersions(prompt.id);
      setVersions(response.versions || []);
    } catch (err) {
      console.error('Error loading versions:', err);
      setError('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreClick = (version) => {
    setVersionToRestore(version);
    setShowConfirmDialog(true);
  };

  const handleRestoreConfirm = async () => {
    if (!canRestore || !versionToRestore) return;
    
    setRestoring(true);
    try {
      await restorePromptVersion(prompt.id, versionToRestore.id);
      setError(null);
      onRestore?.(versionToRestore);
      setShowConfirmDialog(false);
      setVersionToRestore(null);
      onClose();
    } catch (err) {
      console.error('Error restoring version:', err);
      setError('Failed to restore version');
    } finally {
      setRestoring(false);
    }
  };

  const handleViewVersion = (version) => {
    setSelectedVersion(version);
    setShowVersionDetails(true);
    // Load contexts for this version
    loadContextsForVersion(version);
  };

  const loadContextsForVersion = async (version) => {
    // First, check if contexts are already provided as full objects
    if (version.contexts && Array.isArray(version.contexts) && version.contexts.length > 0) {
      const fullContexts = version.contexts.filter(ctx => typeof ctx === 'object' && ctx.id);
      if (fullContexts.length > 0) {
        // Store these contexts directly
        setContexts(prev => {
          const newContexts = { ...prev };
          fullContexts.forEach(ctx => {
            if (ctx.id) {
              newContexts[ctx.id] = ctx;
            }
          });
          return newContexts;
        });
      }
    }

    // Get all context IDs (from contextIds array or extract from contexts)
    const contextIds = version.contextIds || (version.contexts ? version.contexts.map(ctx => {
      if (typeof ctx === 'object' && ctx.id) return ctx.id;
      if (typeof ctx === 'string') return ctx;
      return null;
    }).filter(Boolean) : []);
    
    if (contextIds.length === 0) return;

    // Load each context that we don't already have
    const contextsToLoad = contextIds.filter(id => !contexts[id] && !loadingContexts[id]);
    
    if (contextsToLoad.length === 0) return;

    // Mark as loading
    setLoadingContexts(prev => {
      const newLoading = { ...prev };
      contextsToLoad.forEach(id => { newLoading[id] = true; });
      return newLoading;
    });

    try {
      const loadedContexts = await Promise.all(
        contextsToLoad.map(async (contextId) => {
          try {
            const response = await getContext(contextId);
            // Handle different response structures: response.context, response.data, or response itself
            // The backend returns { context: {...}, permissions: {...} }
            // The API function returns response.data, so we need to check response.context or response.data.context
            const context = response?.context || (response?.data?.context) || response?.data || response;
            
            // Ensure we have a valid context object with an id
            if (context && context.id) {
              return { id: contextId, context };
            }
            return { id: contextId, context: null };
          } catch (error) {
            console.error(`Error loading context ${contextId}:`, error);
            return { id: contextId, context: null };
          }
        })
      );

      // Store loaded contexts
      setContexts(prev => {
        const newContexts = { ...prev };
        loadedContexts.forEach(({ id, context }) => {
          if (context && context.id) {
            newContexts[id] = context;
          }
        });
        return newContexts;
      });
    } catch (error) {
      console.error('Error loading contexts:', error);
    } finally {
      // Mark as no longer loading
      setLoadingContexts(prev => {
        const newLoading = { ...prev };
        contextsToLoad.forEach(id => { delete newLoading[id]; });
        return newLoading;
      });
    }
  };

  const handlePreviewContext = (contextId) => {
    const context = contexts[contextId];
    if (context) {
      setPreviewContext(context);
      setShowContextPreview(true);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getVersionChanges = (version, index) => {
    // First version (index === versions.length - 1) is the original - no changes
    if (index === versions.length - 1) {
      return 'Original version';
    }
    
    // For other versions, compare with the previous version
    const previousVersion = versions[index + 1]; // Versions are ordered newest first
    if (!previousVersion) {
      return 'No changes detected';
    }
    
    const changes = [];
    if (version.title !== previousVersion.title) changes.push('Title');
    if (version.description !== previousVersion.description) changes.push('Description');
    if (version.content !== previousVersion.content || version.prompt !== previousVersion.prompt) changes.push('Content');
    if (JSON.stringify(version.variables) !== JSON.stringify(previousVersion.variables)) changes.push('Variables');
    
    // Check for context changes - compare contextIds or contexts arrays
    const currentContextIds = version.contextIds || (version.contexts ? version.contexts.map(ctx => ctx.id || ctx).filter(Boolean) : []);
    const previousContextIds = previousVersion.contextIds || (previousVersion.contexts ? previousVersion.contexts.map(ctx => ctx.id || ctx).filter(Boolean) : []);
    if (JSON.stringify(currentContextIds.sort()) !== JSON.stringify(previousContextIds.sort())) {
      changes.push(PRODUCT_TERMS.knowledge.titleCase);
    }
    
    if (JSON.stringify(version.files) !== JSON.stringify(previousVersion.files)) changes.push('Files');
    
    return changes.length > 0 ? changes.join(', ') : 'No changes detected';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { 
          minHeight: { xs: '100dvh', sm: '80vh' },
          height: { xs: '100dvh', sm: 'auto' },
          maxHeight: { xs: '100dvh', sm: '90vh' },
          m: { xs: 0, sm: undefined },
          overflow: 'hidden',
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            <Typography variant="h6">
              Version History: {prompt?.title}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : versions.length === 0 ? (
          <Alert severity="info">
            {`No version history available for this ${PRODUCT_TERMS.capability.singular}.`}
          </Alert>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {versions.length} version{versions.length !== 1 ? 's' : ''} found
            </Typography>
            
            <List sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              {versions.map((version, index) => (
                <React.Fragment key={version.id}>
                  <ListItem
                    onClick={() => setSelectedVersion(version)}
                    sx={{
                      border: selectedVersion?.id === version.id ? '2px solid' : '1px solid',
                      borderColor: selectedVersion?.id === version.id ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: selectedVersion?.id === version.id ? 'action.selected' : 'background.paper',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: selectedVersion?.id === version.id ? 'action.selected' : 'action.hover'
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            Version {version.versionNumber}
                          </Typography>
                          {index === 0 && (
                            <Chip 
                              label="Current" 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Stack spacing={1}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ScheduleIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              Created: {formatDate(version.createdAt)}
                            </Typography>
                          </Box>
                          
                          {version.updatedAt && version.updatedAt !== version.createdAt && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <ScheduleIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                Modified: {formatDate(version.updatedAt)}
                              </Typography>
                            </Box>
                          )}
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              Created by {version.createdBy === user?.id ? 'you' : 'another user'}
                            </Typography>
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Changes: {getVersionChanges(version, index)}
                          </Typography>
                        </Stack>
                      }
                    />
                    
                    <ListItemSecondaryAction>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleViewVersion(version)}
                          color={selectedVersion?.id === version.id ? 'primary' : 'default'}
                        >
                          <VisibilityIcon />
                        </IconButton>
                        
                        {canRestore && index > 0 && (
                          <IconButton
                            size="small"
                            onClick={() => handleRestoreClick(version)}
                            disabled={restoring}
                            color="primary"
                          >
                            <RestoreIcon />
                          </IconButton>
                        )}
                      </Stack>
                    </ListItemSecondaryAction>
                  </ListItem>
                  
                  {index < versions.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
        {canRestore && selectedVersion && selectedVersion !== versions[0] && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<RestoreIcon />}
            onClick={() => handleRestoreClick(selectedVersion)}
            disabled={restoring}
          >
            {restoring ? 'Restoring...' : 'Restore This Version'}
          </Button>
        )}
      </DialogActions>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Version Restore
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to restore Version {versionToRestore?.versionNumber}?
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {`This will replace the current ${PRODUCT_TERMS.capability.singular} content with the selected version. This action cannot be undone.`}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            The current version will be saved as a new version before restoration.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRestoreConfirm}
            disabled={restoring}
          >
            {restoring ? 'Restoring...' : 'Yes, Restore Version'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version Details Dialog */}
      <Dialog
        open={showVersionDetails}
        onClose={() => setShowVersionDetails(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Version {selectedVersion?.versionNumber} Details
            </Typography>
            <IconButton onClick={() => setShowVersionDetails(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedVersion && (
            <Stack spacing={3}>
              {/* Title and Description */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  {selectedVersion.title}
                </Typography>
                {selectedVersion.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {selectedVersion.description}
                  </Typography>
                )}
              </Box>

              {/* Capability content - styled like capability display */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  {`${PRODUCT_TERMS.capability.titleCase} Content`}
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedVersion.prompt || selectedVersion.content}
                  </Typography>
                </Paper>
              </Box>

              {selectedVersion.variables && selectedVersion.variables.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Variables
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Grid container spacing={1}>
                      {selectedVersion.variables.map((variable, index) => (
                        <Grid item key={index}>
                          <Chip 
                            label={variable.name} 
                            variant="outlined" 
                            size="small"
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Box>
              )}

              {/* Knowledge - show names with preview buttons */}
              {((selectedVersion.contextIds && selectedVersion.contextIds.length > 0) || 
                (selectedVersion.contexts && selectedVersion.contexts.length > 0)) && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Attached {PRODUCT_TERMS.knowledge.titleCase}
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Stack spacing={1}>
                      {selectedVersion.contextIds && selectedVersion.contextIds.length > 0 ? (
                        selectedVersion.contextIds.map((contextId, index) => {
                          const context = contexts[contextId];
                          const isLoading = loadingContexts[contextId];
                          
                          // Extract context name - handle nested structures
                          const contextName = context?.name || context?.context?.name || PRODUCT_TERMS.knowledge.untitled;
                          const contextDescription = context?.description || context?.context?.description;
                          
                          return (
                            <Box 
                              key={index}
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                p: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'background.paper'
                              }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                {isLoading ? (
                                  <Typography variant="body2" color="text.secondary">
                                    Loading...
                                  </Typography>
                                ) : context ? (
                                  <Typography variant="body2" fontWeight="medium">
                                    {contextName}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    {`${PRODUCT_TERMS.knowledge.titleCase} ID: ${contextId}`}
                                  </Typography>
                                )}
                                {context && contextDescription && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {contextDescription}
                                  </Typography>
                                )}
                              </Box>
                              {context && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => handlePreviewContext(contextId)}
                                  sx={{ ml: 1, flexShrink: 0 }}
                                >
                                  Preview
                                </Button>
                              )}
                            </Box>
                          );
                        })
                      ) : selectedVersion.contexts && selectedVersion.contexts.length > 0 ? (
                        selectedVersion.contexts.map((ctx, index) => {
                          const contextId = typeof ctx === 'object' ? (ctx.id || ctx) : ctx;
                          // Use loaded context if available, otherwise use the context from the version object if it's a full object
                          const context = contexts[contextId] || (typeof ctx === 'object' && ctx.id && ctx.name ? ctx : null);
                          
                          return (
                            <Box 
                              key={index} 
                              sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 2, 
                                p: 1, 
                                border: '1px solid', 
                                borderColor: 'divider', 
                                borderRadius: 1,
                                bgcolor: 'background.paper'
                              }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {context ? (context.name || PRODUCT_TERMS.knowledge.untitled) : (typeof ctx === 'object' && ctx.name ? ctx.name : `${PRODUCT_TERMS.knowledge.titleCase} ${index + 1}`)}
                                </Typography>
                                {(context ? context.description : (typeof ctx === 'object' && ctx.description)) && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {context ? context.description : (typeof ctx === 'object' ? ctx.description : '')}
                                  </Typography>
                                )}
                              </Box>
                              {context && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => handlePreviewContext(contextId)}
                                  sx={{ ml: 1, flexShrink: 0 }}
                                >
                                  Preview
                                </Button>
                              )}
                            </Box>
                          );
                        })
                      ) : selectedVersion.context && selectedVersion.context.length > 0 ? (
                        selectedVersion.context.map((ctx, index) => (
                          <Typography key={index} variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                            {typeof ctx === 'string' ? ctx : JSON.stringify(ctx)}
                          </Typography>
                        ))
                      ) : null}
                    </Stack>
                  </Paper>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', color: 'text.secondary' }}>
                <Typography variant="caption">
                  Created: {formatDate(selectedVersion.createdAt)}
                </Typography>
                <Typography variant="caption">
                  Created by: {selectedVersion.createdBy === user?.id ? 'You' : 'Another user'}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowVersionDetails(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Preview Dialog */}
      <ContextDialog
        open={showContextPreview}
        context={previewContext}
        onClose={() => {
          setShowContextPreview(false);
          setPreviewContext(null);
        }}
      />
    </Dialog>
  );
};

export default VersionHistoryDialog;
